import React, { useContext, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { UserContext, useEntitlements } from "../contexts/index.jsx";
import { canUseOpenedFeature } from "../contexts/openedFeature";
import { SidebarProvider } from "./RCAdmin/SidebarContext.jsx";
import AppFrame from "../components/layout/AppFrame.jsx";
import AppSidebar from "../components/layout/AppSidebar.jsx";
import AppTopbar from "../components/layout/AppTopbar.jsx";
import AppMobileTopbar from "../components/layout/AppMobileTopbar.jsx";
import { PageChromeProvider } from "../components/layout/pageChrome.jsx";
import BottomTabBar from "../components/layout/BottomTabBar.jsx";
import CommandPalette from "../components/ui/CommandPalette.jsx";
import { GlobalCommandPaletteContext } from "../components/ui/globalCommandPalette.js";
import { NAV_PRIMARY, NAV_SECONDARY, NAV_BOTTOM, NAV_DAO, isNavHidden } from "../components/layout/navigation.js";
import MasterStoreBanner from "../components/MasterStoreBanner.jsx";
import { OnboardingWidget } from "../onboarding/index.jsx";
import BetaErrorButton, { useBetaReportingEnabled } from "../beta/BetaErrorButton.jsx";
import QuickChatDock from "../merchantChat/QuickChatDock.jsx";
import { isRcOrDaoStaff } from "../core/config/storeId";
import { useOnMasterStore } from "../helpers/storeScope";
import { canUseWaBroadcast } from "../waBroadcast/waBroadcastHelpers";
import "./RCAdmin/rc-admin.scss";

/**
 * AppShell — Fontaine Phase 2 (M27–M29, M34). Every authenticated route renders inside the new frame:
 *   laptop  → AppSidebar (240px, collapsible) + AppTopbar (search ⌘K · bell · profile) + page
 *   phone   → AppMobileTopbar (search · bell · profile) + page + BottomTabBar (4 tabs + More)
 * The legacy RCSidebar / MobileTopbar / burger drawer are no longer mounted. SidebarProvider stays for
 * the few legacy components that still call useSidebar() (they become no-ops on this shell).
 *
 * M34 mounts the ONE CommandPalette here — the single instance behind the ⌘K affordance the topbars
 * already dispatch (`ft:command-palette`). Its commands are read straight from navigation.js, so the
 * palette can only reach routes the shell already navigates to; plan-locked sections stay visible but
 * inert, matching the sidebar's "locked, never hidden" rule.
 */
const AppShell = ({ children }) => {
  const { user, activeStoreId, accessibleStores } = useContext(UserContext) ?? {};
  // Beta error reporting (master-admin toggle): when on, every store gets the
  // draggable floating reporter. Polled every 5 min so flips propagate.
  const { data: betaReportingOn } = useBetaReportingEnabled(!!user);
  const entitlements = useEntitlements();
  const { hasFeature } = entitlements;
  const waBroadcastOk = canUseWaBroadcast(user, accessibleStores, activeStoreId, hasFeature);
  const onMaster = useOnMasterStore();
  const history = useHistory();
  const showDao = !!user && isRcOrDaoStaff(user, activeStoreId);

  const commands = useMemo(() => {
    const toCommand = (group, item, withShortcut) => ({
      id: item.id,
      label: item.label,
      group,
      icon: item.icon,
      to: item.to,
      disabled: !!item.pageLocked || (!!item.feature && !hasFeature(item.feature)),
      hint: item.pageLocked ? "Locked for now" : !!item.feature && !hasFeature(item.feature) ? "Included in a higher plan" : undefined,
      // The g-then-letter shortcut AppSidebar listens for — shown only where it really works.
      keys: withShortcut && item.shortcut && (!item.feature || hasFeature(item.feature)) ? `G ${item.shortcut.toUpperCase()}` : undefined,
    });
    // Same hidden rows as the sidebar — the palette used to list masterOnly
    // pages (Trade Feed, Deals, Usage Analytics) to every user.
    const canOpen = (key) => canUseOpenedFeature(user, entitlements, key);
    const shown = (i) => !isNavHidden(i, !!user?.is_master_admin, canOpen, hasFeature);
    return [
      ...NAV_PRIMARY.filter((i) => !(i.hideOnMaster && onMaster)).filter(shown).map((i) => toCommand("Go to", i, true)),
      ...NAV_SECONDARY.map((i) => toCommand("Go to", i, false)),
      ...(showDao ? NAV_DAO.map((i) => toCommand("Dao Watches", i, false)) : []),
      ...NAV_BOTTOM.filter((i) => i.id !== "uptime" || hasFeature("uptime_monitor"))
        .filter(shown)
        .filter((i) => !i.waBroadcast || waBroadcastOk)
        .map((i) => toCommand("Account", i, true)),
    ];
  }, [hasFeature, entitlements, user, onMaster, showDao, waBroadcastOk]);

  if (!user) return <>{children}</>;

  return (
    // Declares to the subtree that the global ⌘K palette is already mounted, so a page that ships its
    // own palette demo stands down from the shortcut instead of opening a second dialog on ⌘K.
    // The phone top bar names the page, and PageHeader (rendered deep inside
    // each page) is what knows the name. This carries it upward.
    <PageChromeProvider>
    <GlobalCommandPaletteContext.Provider value>
      <SidebarProvider>
        <AppFrame
          sidebar={<AppSidebar />}
          topbar={<AppTopbar />}
          mobileTopbar={<AppMobileTopbar />}
          bottomBar={<BottomTabBar />}
          overlays={
            <>
              {/* Fixed marker for the unscoped "Master Store" view — covers every page and flow. */}
              <MasterStoreBanner />
              {/* Floating onboarding checklist; renders nothing once dismissed. */}
              <OnboardingWidget />
              {/* Beta error reporter — sits above the onboarding widget, draggable. */}
              {betaReportingOn && <BetaErrorButton />}
              {/* Quick chat: docked panel, or its popped-out PiP window. */}
              <QuickChatDock />
              {/* The one ⌘K palette for the whole app. */}
              <CommandPalette commands={commands} onSelect={(command) => history.push(command.to)} />
            </>
          }
        >
          {/* Legacy hook: rc-admin.scss keys table/focus resets on this class. Neutralised inside the frame. */}
          <div className="rc-app-shell__content ft-frame__legacy">{children}</div>
        </AppFrame>
      </SidebarProvider>
    </GlobalCommandPaletteContext.Provider>
    </PageChromeProvider>
  );
};

export default AppShell;
